// Política do consumo OPERACIONAL da UC — o número que o rateio usa para alocar
// energia, recalculado diariamente a partir das faturas reais.
//
// Existe separado de `consumo_medio_kwh` de propósito. Aquele campo é a decisão
// COMERCIAL congelada da análise cadastral (enviesada pra cima por `ANC-R30`) e
// alimenta faixa de público, comissão e o corte de 700 kWh do pós-venda — mexer
// nele automaticamente moveria dinheiro já pago. Este módulo governa o campo
// novo, que é vivo.
//
// Todo o risco do recálculo DIÁRIO está concentrado aqui:
//   · travamento automático de UC oscilando em torno do limiar
//   · travar com base em amostra pequena demais
//   · escrever todo dia mesmo sem mudança (churn e ruído de auditoria)
//   · apagar um valor bom quando a leitura falha
//   · matar o alerta de delta ao absorver a fatura nova no mesmo dia
//
// Módulo PURO — sem React, sem rede.
//   node --test src/lib/__tests__/consumoOperacional.test.ts

import type { ConfiancaProjecao, ObservacaoMensal } from './projecaoConsumo.ts';

// ── 1. Qual número usar ────────────────────────────────────────────────────

export type FonteConsumo = 'observado' | 'estimativa_cadastral' | 'indisponivel';

export interface ConsumoOperacional {
  /** null quando não há base nenhuma — o chamador precisa tratar, não assumir 0. */
  valorKwh: number | null;
  fonte: FonteConsumo;
  confianca: ConfiancaProjecao;
  /** Texto para a tela. Nunca é null quando a fonte não é 'observado'. */
  aviso: string | null;
  /**
   * O número existe e continua disponível, mas os meses discordam demais entre si
   * para o sistema agir sozinho sobre ele.
   *
   * Sinaliza, não descarta. Levantamento em produção mostrou que 4 das 10 UCs
   * nesta condição são unidades da própria Vigor (Associação e Gestão), que têm
   * papel próprio no rateio — sumir com o número delas cegaria o analista
   * exatamente onde ele precisa enxergar. O que a marca bloqueia é a ação
   * AUTOMÁTICA (pausar a UC), nunca a leitura.
   */
  suspeito: boolean;
}

/**
 * Abaixo disto a média real ainda é frágil demais para alocar energia, e a
 * estimativa da análise cadastral (feita sobre 12 meses de histórico digitado)
 * é a base mais confiável. Três faturas é um piso baixo, alcançado em um
 * trimestre de operação.
 */
export const MESES_MINIMOS_OPERACIONAL = 3;

/**
 * O job é diário; se o observado não é atualizado há mais de um ciclo mensal
 * inteiro com folga, o job parou. Continuar usando o número como se fosse
 * fresco é pior do que voltar para a estimativa cadastral e dizer isso.
 */
export const MAX_DIAS_OBSERVADO = 45;

function diasDesde(quando: string | Date | null | undefined, hoje: Date): number | null {
  if (!quando) return null;
  const d = quando instanceof Date ? quando : new Date(quando);
  const t = d.getTime();
  if (!Number.isFinite(t)) return null;
  return (hoje.getTime() - t) / 86400000;
}

/**
 * Decide qual consumo o rateio deve usar, e diz de onde ele veio.
 *
 * Nunca funde as duas fontes num número só: ou é o observado, ou é a estimativa
 * cadastral declarada como tal, ou é indisponível (`INV-01` — não entregar
 * número plausível e errado em silêncio).
 */
export function resolverConsumoOperacional({
  observadoKwh,
  mesesObservados,
  confianca,
  estimativaCadastralKwh,
  observadoEm,
  hoje = new Date(),
}: {
  observadoKwh: number | null | undefined;
  mesesObservados: number;
  confianca: ConfiancaProjecao;
  estimativaCadastralKwh: number | null | undefined;
  /** Quando o job gravou o observado. Ausente = sem carimbo, tratado como fresco. */
  observadoEm?: string | Date | null;
  hoje?: Date;
}): ConsumoOperacional {
  const dias = diasDesde(observadoEm, hoje);
  const desatualizado = dias != null && dias > MAX_DIAS_OBSERVADO;

  const observadoValido =
    !desatualizado &&
    Number.isFinite(Number(observadoKwh)) &&
    Number(observadoKwh) > 0 &&
    mesesObservados >= MESES_MINIMOS_OPERACIONAL &&
    confianca !== 'sem_dados';

  if (desatualizado) {
    const estimativaStale = Number(estimativaCadastralKwh);
    if (Number.isFinite(estimativaStale) && estimativaStale > 0) {
      return {
        valorKwh: estimativaStale,
        fonte: 'estimativa_cadastral',
        suspeito: false,
        confianca: 'baixa',
        aviso:
          `Consumo observado não é atualizado há ${Math.round(dias!)} dias (o recálculo diário pode ter parado): ` +
          'usando a estimativa da análise cadastral.',
      };
    }
  }

  if (observadoValido) {
    return {
      valorKwh: Number(observadoKwh),
      fonte: 'observado',
      suspeito: false,
      confianca,
      aviso:
        confianca === 'baixa'
          ? `Consumo calculado sobre ${mesesObservados} ${mesesObservados === 1 ? 'fatura' : 'faturas'}, com variação alta — confira antes de alocar.`
          : null,
    };
  }

  const estimativa = Number(estimativaCadastralKwh);
  if (Number.isFinite(estimativa) && estimativa > 0) {
    return {
      valorKwh: estimativa,
      fonte: 'estimativa_cadastral',
      suspeito: false,
      confianca: 'baixa',
      aviso:
        mesesObservados > 0
          ? `Sem histórico suficiente (${mesesObservados} de ${MESES_MINIMOS_OPERACIONAL} faturas): usando a estimativa da análise cadastral.`
          : 'UC sem histórico de faturas: usando a estimativa da análise cadastral.',
    };
  }

  return {
    valorKwh: null,
    fonte: 'indisponivel',
    suspeito: false,
    confianca: 'sem_dados',
    aviso: 'UC sem histórico de faturas e sem estimativa cadastral: consumo indisponível.',
  };
}

// ── 2. Quando escrever (recálculo diário sem churn) ────────────────────────

// A decisão de gravar vive em `_shared/` porque quem mais precisa dela é a
// rotina diária (Deno, não enxerga `src/`). Reexportada aqui para o front usar a
// MESMA regra — a rotina chegou a ter uma cópia inline dela, que é exatamente o
// tipo de duplicação que faz a tela e o job divergirem com o tempo.
export {
  decidirEscritaDiaria,
  TOLERANCIA_ESCRITA_KWH,
  type DecisaoEscrita,
} from './consumoObservado.ts';

// ── 3. Travamento por excesso de saldo, com histerese ──────────────────────

export const LIMIAR_TRAVAR_MESES = 3;

/**
 * Depois que um gestor destrava a UC, o consumo dela normalmente continua o
 * mesmo — então a cobertura continua acima do limiar e a próxima abertura da
 * tela travaria tudo de novo, anulando a decisão dele. A carência dá tempo do
 * rateio do mês entrar e mudar o saldo.
 */
export const CARENCIA_POS_DESTRAVAMENTO_DIAS = 30;

/** Não existe liberação automática: destravar é ato de gestor (`Reativacao`). */
export type AcaoTravamento = 'travar' | 'manter_travada' | 'nenhuma';

export interface AvaliacaoTravamento {
  acao: AcaoTravamento;
  coberturaMeses: number | null;
  motivo: string;
}

/**
 * Decide o travamento automático da UC por excesso de saldo acumulado.
 *
 * O desenho saiu de quatro cenários que quebraram versões anteriores:
 *
 *  S1. UC sem observado — a seleção do valor é a MESMA de
 *      `resolverConsumoOperacional` (observado só com ≥3 meses, senão a
 *      estimativa cadastral). Um `COALESCE` cru mais uma exigência de 3 meses se
 *      contradiziam: caía no estático e depois se recusava a usá-lo, e UCs que
 *      hoje travam deixariam de travar.
 *
 *  S2. UC pausada à mão por outro motivo — nunca é tocada. Só o travamento por
 *      `excesso_saldo` é do sistema; qualquer outra pausa é decisão humana.
 *
 *  S3. Liberação automática NÃO existe. Destravar continua sendo ato de gestor.
 *      O que existe é carência: uma UC recém-destravada não é retravada na hora.
 *
 *  S4. Observado velho (job parado) já cai para a estimativa cadastral em
 *      `resolverConsumoOperacional`, e estimativa não trava.
 */
export function avaliarTravamento({
  saldoCreditosKwh,
  consumo,
  travadaAtualmente,
  motivoTravamento,
  destravadaEm,
  hoje = new Date(),
}: {
  /**
   * ESTOQUE de créditos da UC — `faturas.saldo_ugs_vigor`, o "SALDO KWH" impresso
   * no bloco SCEE. NÃO é `saldo_acumulado_kwh`, que apesar do nome é o resultado
   * do MÊS: a regra passou a existência inteira recebendo aquele campo e por isso
   * nunca pausou uma UC — a cobertura medida assim não alcançava 3 meses nem em
   * tese. Corrigido em 28/08/2026 (migration 20260828140000).
   */
  saldoCreditosKwh: number | null | undefined;
  consumo: ConsumoOperacional;
  travadaAtualmente: boolean;
  /** `rateio_motivo_travamento`. Só 'excesso_saldo' pertence ao sistema. */
  motivoTravamento?: string | null;
  /** `rateio_destravado_em` — última vez que um gestor liberou a UC. */
  destravadaEm?: string | Date | null;
  hoje?: Date;
}): AvaliacaoTravamento {
  // S2: pausa de origem humana é intocável.
  if (travadaAtualmente && motivoTravamento !== 'excesso_saldo') {
    return {
      acao: 'manter_travada',
      coberturaMeses: null,
      motivo: 'UC pausada por decisão humana — o travamento automático não interfere.',
    };
  }

  const saldo = Number(saldoCreditosKwh);
  const base = consumo.valorKwh;

  if (!Number.isFinite(saldo) || base == null || base <= 0) {
    return {
      acao: travadaAtualmente ? 'manter_travada' : 'nenhuma',
      coberturaMeses: null,
      motivo: 'Sem saldo ou sem consumo para calcular cobertura.',
    };
  }

  const coberturaMeses = Math.round((saldo / base) * 100) / 100;

  // S3: já travada por excesso de saldo — permanece. Soltar é ato de gestor.
  if (travadaAtualmente) {
    return {
      acao: 'manter_travada',
      coberturaMeses,
      motivo: `Travada por excesso de saldo (cobertura ${coberturaMeses} meses). A liberação é do gestor.`,
    };
  }

  if (coberturaMeses <= LIMIAR_TRAVAR_MESES) {
    return { acao: 'nenhuma', coberturaMeses, motivo: `Cobertura de ${coberturaMeses} meses dentro do limite.` };
  }

  // S3: não retravar por cima da decisão recente do gestor.
  const diasDesdeDestravamento = diasDesde(destravadaEm, hoje);
  if (diasDesdeDestravamento != null && diasDesdeDestravamento < CARENCIA_POS_DESTRAVAMENTO_DIAS) {
    return {
      acao: 'nenhuma',
      coberturaMeses,
      motivo:
        `Cobertura de ${coberturaMeses} meses, mas a UC foi destravada há ` +
        `${Math.floor(diasDesdeDestravamento)} dias — em carência de ${CARENCIA_POS_DESTRAVAMENTO_DIAS} dias.`,
    };
  }

  // S5: consumo marcado como instável não pausa UC. O número continua visível na
  // tela — marcar não é descartar —, mas pausar tem efeito visível para o cliente
  // e só um gestor reverte; não pode sair de meses que discordam entre si.
  if (consumo.suspeito) {
    return {
      acao: 'nenhuma',
      coberturaMeses,
      motivo:
        `Cobertura de ${coberturaMeses} meses, mas o consumo desta UC está marcado como instável — ` +
        'travamento automático suspenso, revisar manualmente.',
    };
  }

  // Trava só com número em que o próprio sistema confia.
  if (consumo.fonte !== 'observado' || consumo.confianca === 'baixa') {
    return {
      acao: 'nenhuma',
      coberturaMeses,
      motivo:
        `Cobertura de ${coberturaMeses} meses, mas o consumo vem de ` +
        `${consumo.fonte === 'observado' ? 'amostra pouco confiável' : 'estimativa cadastral'} — ` +
        'travamento automático suspenso, revisar manualmente.',
    };
  }

  return { acao: 'travar', coberturaMeses, motivo: `Cobertura de ${coberturaMeses} meses excede ${LIMIAR_TRAVAR_MESES}.` };
}

// ── 4. Como o Financeiro exibe os dois números ─────────────────────────────

export interface LinhaConsumoUc {
  /** `consumo_medio_kwh` — o campo de sempre, intocado. */
  comercialKwh: number | null;
  /** `consumo_observado_kwh` — null quando ainda não há base. */
  observadoKwh: number | null;
  /** Sufixo da coluna nova: '12 faturas', '2 faturas (parcial)', 'sem histórico'. */
  rotuloObservado: string;
  /** Quanto o real difere do cadastrado, em %. Negativo = consome menos que o previsto. */
  divergenciaPercent: number | null;
  /** true quando a diferença passa do que o viés de dimensionamento explica. */
  destacar: boolean;
}

/**
 * O viés pró-cliente já faz o comercial nascer ~16% acima do real, então uma
 * divergência dessa ordem é ESPERADA e não deve chamar atenção. Acima disto o
 * cadastro deixou de descrever o cliente — para mais (subdimensionado) ou para
 * menos (superdimensionado), e os dois casos interessam à operação.
 */
export const LIMIAR_DIVERGENCIA_DESTAQUE = 30;

/**
 * Monta a linha de UC do Financeiro com os DOIS números nomeados.
 *
 * A alternativa — escolher um só — quebra alguma coisa nos dois sentidos: pelo
 * comercial, a tela mente sobre o consumo real; pelo observado, os totais da
 * carteira deixam de bater com comissão e com os relatórios. Exibindo ambos com
 * rótulo, os totais existentes continuam somando o campo de sempre e o operador
 * passa a ver a realidade ao lado.
 */
export function descreverConsumoUc({
  comercialKwh,
  observadoKwh,
  mesesObservados,
}: {
  comercialKwh: number | null | undefined;
  observadoKwh: number | null | undefined;
  mesesObservados: number;
}): LinhaConsumoUc {
  const comercial = Number(comercialKwh);
  const observado = Number(observadoKwh);
  const temComercial = Number.isFinite(comercial) && comercial > 0;
  const temObservado = Number.isFinite(observado) && observado > 0 && mesesObservados > 0;

  let rotuloObservado: string;
  if (!temObservado) rotuloObservado = 'sem histórico';
  else if (mesesObservados < MESES_MINIMOS_OPERACIONAL)
    rotuloObservado = `${mesesObservados} ${mesesObservados === 1 ? 'fatura' : 'faturas'} (parcial)`;
  else rotuloObservado = `${mesesObservados} faturas`;

  const divergenciaPercent =
    temComercial && temObservado ? Math.round(((observado - comercial) / comercial) * 100) : null;

  return {
    comercialKwh: temComercial ? comercial : null,
    observadoKwh: temObservado ? observado : null,
    rotuloObservado,
    divergenciaPercent,
    destacar:
      divergenciaPercent != null &&
      mesesObservados >= MESES_MINIMOS_OPERACIONAL &&
      Math.abs(divergenciaPercent) >= LIMIAR_DIVERGENCIA_DESTAQUE,
  };
}

// ── 5. Por que esta UC não tem histórico ───────────────────────────────────

export type MotivoSemHistorico =
  | 'nunca_calculado'
  | 'uc_nova'
  | 'faturas_descartadas'
  | 'faturas_nao_encontradas';

export interface AusenciaDeHistorico {
  motivo: MotivoSemHistorico;
  texto: string;
  /** true quando é sinal de problema de cadastro, não situação normal de operação. */
  requerAtencao: boolean;
}

/** UC ativa por mais tempo que isto já deveria ter faturado pelo menos uma vez. */
export const DIAS_ESPERADOS_ATE_PRIMEIRA_FATURA = 90;

/**
 * Traduz "sem histórico" no motivo real.
 *
 * A célula vazia esconde casos muito diferentes: UC que acabou de entrar e ainda
 * não faturou é rotina; UC ativa há meses sem nenhuma fatura associada é quase
 * sempre número de UC divergente — as faturas existem no sistema, gravadas sob
 * outro número, e nunca vão casar. Esse caso é conhecido e caro (é o mesmo que
 * quebra a deduplicação de faturas, `FIN-R10`), e some se a tela só disser "—".
 */
export function explicarAusenciaDeHistorico({
  observadoEm,
  diasDesdeCadastro,
  faturasDescartadas = 0,
  situacao,
}: {
  /** `consumo_observado_em`. Ausente = o recálculo ainda não passou por esta UC. */
  observadoEm?: string | Date | null;
  /** Idade do cadastro da UC, em dias. */
  diasDesdeCadastro?: number | null;
  /** Faturas que a UC tem, mas que não puderam ser usadas (mês ou consumo ilegível). */
  faturasDescartadas?: number;
  situacao?: string | null;
}): AusenciaDeHistorico {
  if (faturasDescartadas > 0) {
    return {
      motivo: 'faturas_descartadas',
      texto:
        `Esta UC tem ${faturasDescartadas} ${faturasDescartadas === 1 ? 'fatura' : 'faturas'}, mas ` +
        `${faturasDescartadas === 1 ? 'ela não pôde' : 'nenhuma delas pôde'} ser usada — mês de referência ` +
        'ou consumo ilegível.',
      requerAtencao: true,
    };
  }

  if (!observadoEm) {
    return {
      motivo: 'nunca_calculado',
      texto: 'O recálculo diário ainda não passou por esta UC.',
      requerAtencao: false,
    };
  }

  const nova =
    (situacao || '').trim().toLowerCase().startsWith('1') ||
    (typeof diasDesdeCadastro === 'number' && diasDesdeCadastro < DIAS_ESPERADOS_ATE_PRIMEIRA_FATURA);

  if (nova) {
    return {
      motivo: 'uc_nova',
      texto: 'UC no primeiro ciclo: ainda não houve faturamento. É esperado.',
      requerAtencao: false,
    };
  }

  const idade = typeof diasDesdeCadastro === 'number' ? `há ${Math.round(diasDesdeCadastro)} dias` : 'há meses';
  return {
    motivo: 'faturas_nao_encontradas',
    texto:
      `UC cadastrada ${idade} e nenhuma fatura foi localizada para este número. ` +
      'Confira se o número da UC está correto: fatura gravada com outro número nunca é associada a ela.',
    requerAtencao: true,
  };
}

// ── 6. Baseline do alerta de delta ─────────────────────────────────────────

/**
 * Média das observações EXCLUINDO o mês mais recente.
 *
 * O alerta de variação compara a fatura nova com a média. Se a média já
 * incorporou essa mesma fatura — e com recálculo diário ela incorpora no mesmo
 * dia — o alerta passa a comparar o mês consigo mesmo e nunca dispara. Tirando
 * o mês mais novo do baseline, ele volta a medir "este mês contra o histórico".
 *
 * Devolve null quando não sobra histórico suficiente para comparar.
 */
export function baselineDelta(observacoes: ObservacaoMensal[] | null | undefined): number | null {
  const lista = (Array.isArray(observacoes) ? observacoes : [])
    .filter((o) => Number.isFinite(Number(o?.consumoKwh)) && Number(o.consumoKwh) > 0)
    .sort((a, b) => a.ordinal - b.ordinal);

  const anteriores = lista.slice(0, -1);
  if (anteriores.length === 0) return null;

  const soma = anteriores.reduce((acc, o) => acc + Number(o.consumoKwh), 0);
  return Math.round(soma / anteriores.length);
}
